import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Banknote, Download, Plus, Loader2, Pencil, CheckCircle2, Ban, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';
import ReferralSplits from '../../components/payouts/ReferralSplits';   // 10/20: computed 2% pool × referrer split

// Josh 10/08: "Under Syndications there should be a section called Payouts — this is
// where all the money out for syndicators and referrers go." Mirrors his payouts
// sheet. Syndicator withdrawals and partner commissions are READ from their own
// ledgers (approve / mark-paid stay on those pages); referrer payouts are recorded
// here — the amount is typed, never computed (the split % per referrer is not in
// the CRM; see CLAUDE.md open questions).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  syndicator_withdrawal: 'Syndicator withdrawal',
  partner_commission: 'Partner commission',
  referrer_payout: 'Referrer payout',
  iso_commission: 'ISO commission',
};
const KIND_BADGE = {
  syndicator_withdrawal: 'bg-orange-100 text-orange-800',
  partner_commission: 'bg-purple-100 text-purple-800',
  referrer_payout: 'bg-emerald-100 text-emerald-800',
  iso_commission: 'bg-teal-100 text-teal-800',
};
const METHODS = ['Zelle', 'Wire', 'ACH', 'Check', 'MCA Suite deduction', 'Other'];
const fmtMDY = (d) => (d && d.length >= 10 ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : d || '—');
const monthStart = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = { referrer_contact_id: '', amount: '', date: today(), deal_id: '', method: 'Zelle', reference: '', notes: '', status: 'Paid' };

export default function SyndicationPayouts({ embedded = false }) {
  // 11/03 (Josh: consolidate pages): `embedded` renders the body without the app shell, inside a hub tab.
  const Wrap = embedded ? React.Fragment : Layout;
  const { getAuthHeaders, user } = useAuth();
  const isSuperAdmin = !!user?.roles?.includes('super_admin');

  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState('');
  const [kind, setKind] = useState('all');
  const [payee, setPayee] = useState('all');
  const [data, setData] = useState({ rows: [], summary: null, referrers: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // referrer payout row being edited (null = create)
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (kind !== 'all') params.kind = kind;
      if (payee !== 'all') params.payee_id = payee;
      const res = await axios.get(`${API}/payouts`, { params, headers: getAuthHeaders() });
      setData(res.data || { rows: [], summary: null, referrers: [] });
    } catch (e) {
      toast.error(`Failed to load payouts: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [from, to, kind, payee, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const payees = useMemo(() => (data.summary?.by_payee || []).map((p) => ({ id: p.payee_id, name: p.payee_name, type: p.payee_type })), [data]);
  const s = data.summary || { total_paid: 0, pending_referrer: 0, by_kind: {}, by_payee: [] };

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM, date: today() }); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ referrer_contact_id: row.payee_id || '', amount: String(row.amount ?? ''), date: row.date || today(),
      deal_id: row.deal_id_number != null ? String(row.deal_id_number) : '', method: row.method || 'Other',
      reference: row.reference || '', notes: row.notes || '', status: row.status || 'Paid' });
    setDialogOpen(true);
  };

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!editing && !form.referrer_contact_id) { toast.error('Pick a referrer'); return; }
    if (!(amount > 0)) { toast.error('Amount must be greater than 0'); return; }
    if (!form.date) { toast.error('Date is required'); return; }
    setSaving(true);
    try {
      const body = { amount, date: form.date, deal_id: form.deal_id.trim(), method: form.method, reference: form.reference, notes: form.notes, status: form.status };
      if (editing) {
        await axios.put(`${API}/payouts/referrer/${editing.source_id}`, body, { headers: getAuthHeaders() });
        toast.success('Referrer payout updated');
      } else {
        await axios.post(`${API}/payouts/referrer`, { ...body, referrer_contact_id: form.referrer_contact_id, deal_id: body.deal_id || null }, { headers: getAuthHeaders() });
        toast.success('Referrer payout recorded');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (row, status) => {
    if (status === 'Void' && !window.confirm(`Void this ${formatCurrency(row.amount)} payout to ${row.payee_name}? It will leave the ledger (the record is kept).`)) return;
    setBusyId(row.source_id);
    try {
      await axios.put(`${API}/payouts/referrer/${row.source_id}`, { status }, { headers: getAuthHeaders() });
      toast.success(status === 'Paid' ? 'Marked paid' : 'Voided');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Permanently delete this ${formatCurrency(row.amount)} payout to ${row.payee_name}? Void is the normal way to retract one.`)) return;
    setBusyId(row.source_id);
    try {
      await axios.delete(`${API}/payouts/referrer/${row.source_id}`, { headers: getAuthHeaders() });
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await axios.get(`${API}/payouts/export.csv`, { params, headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `payouts_${from || 'all'}_${to || 'all'}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <Wrap>
      <div className="space-y-6" data-testid="payouts-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Banknote className="h-6 w-6 text-emerald-600" /> Payouts</h1>
            <p className="text-sm text-slate-500 mt-1">
              Every dollar out — syndicator withdrawals, ISO commissions, partner commissions and referrer payouts — in one ledger.
              Withdrawals are approved on <Link to="/syndication-withdrawals" className="underline">Withdrawals</Link>; ISO commissions are paid on <Link to="/commissions" className="underline">Commissions</Link>; partner commissions are marked paid on the <Link to="/partner-ledger" className="underline">Partner Ledger</Link>; referrer payouts are recorded here.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCsv} data-testid="payouts-export"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
            <Button onClick={openCreate} data-testid="payouts-add-referrer"><Plus className="h-4 w-4 mr-2" /> Record referrer payout</Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="payouts-from" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="payouts-to" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="w-52" data-testid="payouts-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payee</Label>
              <Select value={payee} onValueChange={setPayee}>
                <SelectTrigger className="w-56" data-testid="payouts-payee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payees</SelectItem>
                  {payees.filter((p) => p.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); setKind('all'); setPayee('all'); }}>All time</Button>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            ['Total paid out', s.total_paid, 'text-slate-900'],
            ['Syndicator withdrawals', s.by_kind?.syndicator_withdrawal || 0, 'text-orange-700'],
            ['ISO commissions', s.by_kind?.iso_commission || 0, 'text-teal-700'],
            ['Partner commissions', s.by_kind?.partner_commission || 0, 'text-purple-700'],
            ['Referrer payouts', s.by_kind?.referrer_payout || 0, 'text-emerald-700'],
            ['Referrer pending', s.pending_referrer || 0, 'text-amber-700'],
          ].map(([label, value, cls]) => (
            <Card key={label}>
              <CardHeader className="pb-1"><CardTitle className="text-xs font-medium text-slate-500">{label}</CardTitle></CardHeader>
              <CardContent><p className={`text-xl font-bold ${cls}`}>{formatCurrency(value)}</p></CardContent>
            </Card>
          ))}
        </div>

        {/* By payee */}
        {s.by_payee?.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">By payee {from || to ? `(${from ? fmtMDY(from) : '…'} – ${to ? fmtMDY(to) : 'today'})` : '(all time)'}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Payee</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Pending</TableHead><TableHead className="text-right">Rows</TableHead></TableRow></TableHeader>
                <TableBody>
                  {s.by_payee.map((p) => (
                    <TableRow key={p.payee_id || p.payee_name} className="cursor-pointer hover:bg-slate-50" onClick={() => p.payee_id && setPayee(p.payee_id)}>
                      <TableCell className="font-medium">{p.payee_name}</TableCell>
                      <TableCell><Badge variant="outline">{p.payee_type}</Badge></TableCell>
                      <TableCell className="text-right">{formatCurrency(p.paid)}</TableCell>
                      <TableCell className="text-right text-amber-700">{p.pending ? formatCurrency(p.pending) : '—'}</TableCell>
                      <TableCell className="text-right text-slate-500">{p.rows}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Ledger */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Ledger · {data.count || 0} row(s)</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
            ) : (data.rows || []).length === 0 ? (
              <div className="py-12 text-center text-slate-500">No payouts in this window.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Payee</TableHead><TableHead>Deal</TableHead>
                      <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Method / Ref</TableHead><TableHead>Notes</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((r) => (
                      <TableRow key={`${r.kind}-${r.source_id}`} data-testid={`payout-row-${r.source_id}`}>
                        <TableCell className="whitespace-nowrap">{fmtMDY(r.date)}</TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded ${KIND_BADGE[r.kind] || 'bg-slate-100'}`}>{KIND_LABEL[r.kind] || r.kind}</span></TableCell>
                        <TableCell className="font-medium">{r.payee_name}</TableCell>
                        <TableCell>{r.deal_id ? <Link to={`/deals/${r.deal_id}`} className="text-blue-700 underline">#{r.deal_id_number} {r.deal_name || ''}</Link> : <span className="text-slate-400">—</span>}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(r.amount)}</TableCell>
                        <TableCell>{r.status === 'Pending' ? <Badge className="bg-amber-100 text-amber-800">Pending</Badge> : <Badge className="bg-green-100 text-green-800">Paid</Badge>}</TableCell>
                        <TableCell className="text-xs text-slate-600">{[r.method, r.reference].filter(Boolean).join(' · ') || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-[16rem] truncate" title={r.notes || ''}>{r.notes || ''}</TableCell>
                        <TableCell className="whitespace-nowrap text-right">
                          {r.kind === 'referrer_payout' && (
                            <div className="inline-flex gap-1">
                              {r.status === 'Pending' && <Button size="sm" variant="ghost" title="Mark paid" disabled={busyId === r.source_id} onClick={() => setStatus(r, 'Paid')}><CheckCircle2 className="h-4 w-4 text-green-700" /></Button>}
                              <Button size="sm" variant="ghost" title="Edit" disabled={busyId === r.source_id} onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" title="Void" disabled={busyId === r.source_id} onClick={() => setStatus(r, 'Void')}><Ban className="h-4 w-4 text-amber-700" /></Button>
                              {isSuperAdmin && <Button size="sm" variant="ghost" title="Delete (super admin)" disabled={busyId === r.source_id} onClick={() => remove(r)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Record / edit referrer payout */}
        <ReferralSplits />

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit referrer payout' : 'Record referrer payout'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {!editing && (
                <div>
                  <Label>Referrer</Label>
                  {data.referrers?.length ? (
                    <Select value={form.referrer_contact_id} onValueChange={(v) => setForm({ ...form, referrer_contact_id: v })}>
                      <SelectTrigger data-testid="referrer-select"><SelectValue placeholder="Pick a referrer" /></SelectTrigger>
                      <SelectContent>{data.referrers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-amber-700">No contact has the Referrer type yet. Open the contact in <Link to="/contacts" className="underline">Contacts</Link>, Edit, and tick <b>Referrer</b>.</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="referrer-amount" /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="referrer-date" /></div>
                <div><Label>Deal # (optional)</Label><Input value={form.deal_id} onChange={(e) => setForm({ ...form, deal_id: e.target.value })} placeholder="e.g. 10973" data-testid="referrer-deal" /></div>
                <div>
                  <Label>Method</Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="wire / Zelle ref" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Paid">Paid</SelectItem><SelectItem value="Pending">Pending</SelectItem>{editing && <SelectItem value="Void">Void</SelectItem>}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <p className="text-xs text-slate-500">The amount is what you actually paid or owe — nothing is computed from the 2% fee here.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving || (!editing && !data.referrers?.length)} data-testid="referrer-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save' : 'Record')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Wrap>
  );
}
